import * as amplitude from '@amplitude/analytics-browser';

import type { AnalyticsControllerType } from './AnalyticsController.types';

import type { User } from '../../model/domain/User';

type AmplitudeEvent =
  | { name: 'Login - attempted'; properties: { login_type: 'SSO' | 'password' } }
  | { name: 'Login - pageview'; properties: undefined }
  | { name: 'Signup - attempted'; properties: undefined }
  | { name: 'Signup - pageview'; properties: undefined }
  | { name: 'MainStudyCycle - attempted'; properties: { main_cycle_length: string } }
  | {
      name: 'MainStudyCycle - completed';
      properties: {
        main_cycle_completed: '0' | '1';
        main_cycle_duration: string;
      };
    }
  | { name: 'StudyCycle - pageview'; properties: undefined }
  | { name: 'SurveyCycle - ready to review'; properties: undefined }
  | { name: 'SurveyCycle - attempted'; properties: { survey_cycle_length: string } }
  | {
      name: 'SurveyCycle - completed';
      properties: {
        survey_cycle_completed: '0' | '1';
        survey_cycle_duration: string;
      };
    }
  | { name: 'PerformanceScreen - pageview'; properties: undefined }
  | { name: 'PerformanceScreen - whatIsTKL'; properties: undefined }
  | { name: 'Review10more - attempted'; properties: { review10more_cycle_length: string } }
  | {
      name: 'Review10more - completed';
      properties: {
        review10more_cycle_completed: '0' | '1';
        review10more_cycle_duration: string;
      };
    }
  | { name: 'LibraryMode - pageview'; properties: undefined }
  | { name: 'LibraryMode - selectDeck'; properties: { deck_name: string } }
  | { name: 'LibraryMode - attempted'; properties: { deck_name: string; mode: 'Review' | 'Study' } }
  | { name: 'Tour - pageview'; properties: undefined }
  | { name: 'Feedback - pageview'; properties: undefined }
  | { name: 'Feedback - submit'; properties: undefined }
  | { name: 'Preferences - pageview'; properties: undefined }
  | { name: 'Preferences - submit'; properties: undefined }
  | { name: 'AfterLoginResetPassword - pageview'; properties: undefined }
  | { name: 'ResetPassword - submit'; properties: undefined }
  | { name: 'Logout - pageview'; properties: undefined }
  | { name: 'BeforeLoginResetPassword - pageview'; properties: undefined }
  | { name: 'BeforeLoginResetPassword - submit'; properties: undefined };

export class AmplitudeAnalyticsController implements Partial<AnalyticsControllerType> {
  constructor(apiKey: string) {
    amplitude.init(apiKey, { autocapture: { pageViews: false } });
  }

  private trackEvent = <T extends AmplitudeEvent['name']>(
    eventName: T,
    eventProperties: Extract<AmplitudeEvent, { name: T }>['properties']
  ) => {
    amplitude.track(eventName, eventProperties);
  };

  identifyUser: AnalyticsControllerType['identifyUser'] = async (user: User) => {
    amplitude.setUserId(String(user.id).padStart(5, '0'));
    const identifyEvent = new amplitude.Identify();

    identifyEvent.set('partner', user.partner.name);
    amplitude.identify(identifyEvent);
  };

  resetUser: AnalyticsControllerType['resetUser'] = () => {
    amplitude.reset();
  };

  loginPageView: AnalyticsControllerType['loginPageView'] = () =>
    this.trackEvent('Login - pageview', undefined);

  loginAttempted: AnalyticsControllerType['loginAttempted'] = (login_type) =>
    this.trackEvent('Login - attempted', { login_type });

  signupPageView: AnalyticsControllerType['signupPageView'] = () =>
    this.trackEvent('Signup - pageview', undefined);

  signupAttempted: AnalyticsControllerType['signupAttempted'] = () =>
    this.trackEvent('Signup - attempted', undefined);

  mainStudyCycleAttempted: AnalyticsControllerType['mainStudyCycleAttempted'] = (
    main_cycle_length
  ) => this.trackEvent('MainStudyCycle - attempted', { main_cycle_length });

  mainStudyCycleCompleted: AnalyticsControllerType['mainStudyCycleCompleted'] = (
    main_cycle_completed,
    main_cycle_duration
  ) =>
    this.trackEvent('MainStudyCycle - completed', {
      main_cycle_completed,
      main_cycle_duration,
    });

  studyCyclePageView: AnalyticsControllerType['studyCyclePageView'] = () =>
    this.trackEvent('StudyCycle - pageview', undefined);

  surveyCycleReadyToReview: AnalyticsControllerType['surveyCycleReadyToReview'] = () =>
    this.trackEvent('SurveyCycle - ready to review', undefined);

  surveyCycleAttempted: AnalyticsControllerType['surveyCycleAttempted'] = (survey_cycle_length) =>
    this.trackEvent('SurveyCycle - attempted', { survey_cycle_length });

  surveyCycleCompleted: AnalyticsControllerType['surveyCycleCompleted'] = (
    survey_cycle_completed,
    survey_cycle_duration
  ) =>
    this.trackEvent('SurveyCycle - completed', {
      survey_cycle_completed,
      survey_cycle_duration,
    });

  performanceScreenPageView: AnalyticsControllerType['performanceScreenPageView'] = () =>
    this.trackEvent('PerformanceScreen - pageview', undefined);

  performanceScreenWhatIsTKL: AnalyticsControllerType['performanceScreenWhatIsTKL'] = () =>
    this.trackEvent('PerformanceScreen - whatIsTKL', undefined);

  review10moreAttempted: AnalyticsControllerType['review10moreAttempted'] = (
    review10more_cycle_length
  ) => this.trackEvent('Review10more - attempted', { review10more_cycle_length });

  review10moreCompleted: AnalyticsControllerType['review10moreCompleted'] = (
    review10more_cycle_completed,
    review10more_cycle_duration
  ) =>
    this.trackEvent('Review10more - completed', {
      review10more_cycle_completed,
      review10more_cycle_duration,
    });

  libraryModePageView: AnalyticsControllerType['libraryModePageView'] = () =>
    this.trackEvent('LibraryMode - pageview', undefined);

  libraryModeSelectDeck: AnalyticsControllerType['libraryModeSelectDeck'] = (deck_name) =>
    this.trackEvent('LibraryMode - selectDeck', { deck_name });

  libraryModeAttempted: AnalyticsControllerType['libraryModeAttempted'] = (deck_name, mode) =>
    this.trackEvent('LibraryMode - attempted', { deck_name, mode });

  tourPageView: AnalyticsControllerType['tourPageView'] = () =>
    this.trackEvent('Tour - pageview', undefined);

  feedbackPageView: AnalyticsControllerType['feedbackPageView'] = () =>
    this.trackEvent('Feedback - pageview', undefined);

  feedbackSubmit: AnalyticsControllerType['feedbackSubmit'] = () =>
    this.trackEvent('Feedback - submit', undefined);

  preferencesPageView: AnalyticsControllerType['preferencesPageView'] = () =>
    this.trackEvent('Preferences - pageview', undefined);

  preferencesSubmit: AnalyticsControllerType['preferencesSubmit'] = () =>
    this.trackEvent('Preferences - submit', undefined);

  afterLoginResetPasswordPageView: AnalyticsControllerType['afterLoginResetPasswordPageView'] =
    () => this.trackEvent('AfterLoginResetPassword - pageview', undefined);

  resetPasswordSubmit: AnalyticsControllerType['resetPasswordSubmit'] = () =>
    this.trackEvent('ResetPassword - submit', undefined);

  logoutPageView: AnalyticsControllerType['logoutPageView'] = () => {
    this.trackEvent('Logout - pageview', undefined);
  };

  beforeLoginResetPasswordPageView: AnalyticsControllerType['beforeLoginResetPasswordPageView'] =
    () => this.trackEvent('BeforeLoginResetPassword - pageview', undefined);

  beforeLoginResetPasswordSubmit: AnalyticsControllerType['beforeLoginResetPasswordSubmit'] = () =>
    this.trackEvent('BeforeLoginResetPassword - submit', undefined);
}
